import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFcmTokenWithPermissionCheck } from '../../../libs/firebase';
import { useSaveUserDevice } from '../services';
import { getDeviceInfo } from '../../../utils';
import { openPushNotificationDialogs } from '../pushNotificationsSlice';
import {
  NOTIFICATION_TYPES,
  PUSH_PERMISSION_PROMPT_INTERVAL_MS,
} from '../../../config/consts/commons';
import { setMessage } from '../../toasts/toastSlice';
import { ROLES } from '@wareclouds/frontend-common/b2c';
import { isSupported } from 'firebase/messaging';
import { useTranslation } from 'react-i18next';
import env from '../../../config/env';

const intervalFromEnv = Number(env.PUSH_PERMISSION_PROMPT_INTERVAL_MS);
const pushNotificationShowDialogInterval = isNaN(intervalFromEnv)
  ? PUSH_PERMISSION_PROMPT_INTERVAL_MS
  : intervalFromEnv;

export function usePushNotificationPermission() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['system']);
  const { isAuthorized, username } = useSelector((state) => state.auth);
  const { permission: savedPermission } = useSelector(
    (state) => state.pushNotification
  );
  const { mutate: saveUserDevice } = useSaveUserDevice();

  const hasSavedUserDeviceRef = useRef(false);
  const intervalRef = useRef(null);

  const showErrorToast = useCallback(
    (key) => {
      dispatch(
        setMessage({
          body: t(key, { ns: 'system' }),
          type: NOTIFICATION_TYPES.ERROR,
        })
      );
    },
    [dispatch, t]
  );

  const saveDevice = useCallback(async () => {
    if (hasSavedUserDeviceRef.current) return;

    try {
      const { token } = await getFcmTokenWithPermissionCheck();
      if (!token) throw new Error('NOT_AVAILABLE_TOKEN');
      saveUserDevice({
        id_user: username,
        id_role: ROLES.CLOUDER,
        token_device: token,
        device_info: getDeviceInfo(),
      });
      hasSavedUserDeviceRef.current = true;
    } catch (error) {
      showErrorToast(error.message);
    }
  }, [saveUserDevice, showErrorToast, username]);

  const openModal = useCallback(() => {
    dispatch(openPushNotificationDialogs('showPushPermissionModal'));
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthorized) return;

    const init = async () => {
      const supported = await isSupported();

      if (!supported || typeof Notification === 'undefined') {
        console.info('Push notifications not supported');
        return;
      }

      const permission = Notification.permission;
      if (permission === 'granted') {
        await saveDevice();
        return;
      }

      openModal();

      intervalRef.current = setInterval(() => {
        const currentPermission = Notification.permission;
        if (currentPermission === 'granted') {
          clearInterval(intervalRef.current);
        } else {
          openModal();
        }
      }, pushNotificationShowDialogInterval);
    };

    init();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      hasSavedUserDeviceRef.current = false;
    };
  }, [isAuthorized, openModal, saveDevice, savedPermission]);

  return { openModal };
}
